import {
  AnalyticsEvent,
  OutboundLink,
  trackCustomEvent,
} from '../../utils/analytics';
import { HTMLAttributes, useCallback, useMemo, useState } from 'react';
import { useOpenCollectiveContext } from '../../context/OpenCollectiveContext';
import { formatPrice } from '../../utils/priceFormatter';
import {
  OpenCollectiveEmbedModal,
  OPEN_COLLECTIVE_EMBED_MODAL_ID,
} from './OpenCollectiveEmbedModal';
import { animated, config, useTransition } from 'react-spring';

const FIREBASE_DB_URL = 'https://dfua-b7e3e.firebaseio.com/messages.json';
const EBRD_MODAL_ID = 'ebrd-modal';

interface Ticket {
  label: string;
  price: number;
  url: string;
  slug: string;
  description: string;
}

const TICKETS: Ticket[] = [
  {
    label: '$25',
    price: 25,
    url: 'https://opencollective.com/devfest-for-ukraine/events/devfest-for-ukraine-93217149/order/41282',
    slug: 'donate-41282',
    description: 'For $25 Nova Ukraine can buy groceries to feed a family',
  },
  {
    label: '$75',
    price: 75,
    url: 'https://opencollective.com/devfest-for-ukraine/events/devfest-for-ukraine-93217149/order/40558',
    slug: 'donate-40558',
    description:
      'For $75 Voices of Children can arrange a psychological rehabilitation group for 3 kids',
  },
  {
    label: '$125',
    price: 125,
    url: 'https://opencollective.com/devfest-for-ukraine/events/devfest-for-ukraine-93217149/order/40556',
    slug: 'donate-40556',
    description:
      'For $125 Come Back Alive can buy a fully equipped first-aid kit for a field medic',
  },
  {
    label: '$250',
    price: 250,
    url: 'https://opencollective.com/devfest-for-ukraine/events/devfest-for-ukraine-93217149/order/40557',
    slug: 'donate-40557',
    description:
      "For $250 Nova Ukraine can buy a month's supply of insulin to treat diabetes for 150 people",
  },
  {
    label: '$500',
    price: 500,
    url: 'https://opencollective.com/devfest-for-ukraine/events/devfest-for-ukraine-93217149/order/40559',
    slug: 'donate-40559',
    description:
      'For $500 Voices of Children can buy equipment for the physical rehabilitation of an injured child',
  },
  {
    label: 'Custom',
    price: 0,
    url: 'https://opencollective.com/devfest-for-ukraine/events/devfest-for-ukraine-93217149/order/40590',
    slug: 'donate-40590',
    description: 'Every amount makes a difference',
  },
];

interface TicketsProps {
  showTitle?: boolean;
  showDescription?: boolean;
  className?: HTMLAttributes<HTMLDivElement>['className'];
}

export const Tickets = ({
  showTitle = true,
  showDescription = true,
  className,
}: TicketsProps) => {
  const [selectedTicket, setTicket] = useState<Ticket | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isModalOpened, setModalOpened] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [hasBoughtTicket, setHasBoughtTicket] = useState<boolean>(false);
  const { statistic, error } = useOpenCollectiveContext();

  const averageDonation = useMemo(() => {
    const average =
      statistic?.totalNetAmountReceived && statistic?.totalCount
        ? statistic.totalNetAmountReceived / statistic.totalCount
        : 0;
    return formatPrice(average);
  }, [statistic]);

  const onProceed = useCallback(() => {
    if (message) {
      fetch(FIREBASE_DB_URL, {
        method: 'POST',
        body: JSON.stringify({
          message,
          ticket: selectedIndex !== null ? TICKETS[selectedIndex].price : -1,
          timestamp: Date.now(),
        }),
      });
    }
    setModalOpened(true);
    trackCustomEvent({
      category: 'Tickets',
      action: AnalyticsEvent.OpenPaymentFlow,
      label: 'Proceed to payment',
    });
  }, [message, selectedIndex]);

  const onCloseModal = useCallback(() => {
    setModalOpened(false);

    // setSelectedIndex(null);
    // setTicket(null);
    // setMessage('');
    // setHasBoughtTicket(true);
  }, []);

  const buyAnotherTicket = useCallback(() => {
    // setHasBoughtTicket(false);
    trackCustomEvent({
      category: 'Tickets',
      action: AnalyticsEvent.MakeAnotherDonation,
      label: 'Make another donation',
    });
  }, []);

  const transitions = useTransition(selectedTicket, {
    key: selectedTicket?.slug,
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: config.stiff,
  });

  return (
    <>
      <div
        id="tickets"
        className={`container mx-auto py-12 md:py-24 px-4 flex flex-col md:items-center reveal fade-bottom ${className}`}
      >
        {showTitle && (
          <h2 className="font-display text-4xl md:text-5xl lg:text-6.5xl md:text-center">
            Dev<span className="font-display-alt">Fest</span> is over.
            <br />
            To<span className="font-display-alt">ge</span>ther we raised $130K for three NGOs in Ukraine
          </h2>
        )}

        {showDescription && (
          <div className="mt-4 md:mt-8 md:w-10/12 lg:w-8/12 xl:w-6/12 flex flex-col md:items-center">
            <p className="text-lg md:text-center opacity-80">
              While DevFest is over Russian aggresion in Ukraine continues. 
              As a next step, see our report on the use of funds, check the session recordings, 
              and consider donating to continue supporting Ukrainian people.
              <br/>
              Together we win 🇺🇦
            </p>
          </div>
        )}


        <div className="w-full lg:w-10/12 xl:w-8/12 mt-4 md:mt-8 flex flex-col md:items-center">
          <OutboundLink
              className="mt-4 md:mt-8 btn btn-primary modal-button"
              href="https://www.facebook.com/devfest.ukraine/posts/pfbid02WLfKcBuwN3A1kmFb7q5Pte8ULMdFambUrVzWYjaG6PwJP44Pie7GjSNCkh2DoDCCl"
              target="_blank"
            >
              Report on fund distribution
          </OutboundLink>
          <OutboundLink
              className="mt-4 md:mt-8 btn btn-primary modal-button"
              href="https://www.youtube.com/playlist?list=PLt8lEzcLNl33BZFkdWdrHGfRgVBoPwdn5"
              target="_blank"
            >
              Session recordings
          </OutboundLink>
          <OutboundLink
              className="mt-4 md:mt-8 btn btn-primary modal-button"
              href="https://u24.gov.ua/"
              target="_blank"
            >
              Donate at United24
          </OutboundLink>
          
        </div>
        {/* {TICKETS.length ? (
          <div className="w-full lg:w-10/12 xl:w-8/12 mt-4 md:mt-8 flex flex-col md:items-center">
            {hasBoughtTicket ? (
              <>
                <p className="mt-4 text-2xl md:text-3xl">Thank you 💙💛</p>
                <span
                  className="mt-2 underline underline-offset-4 cursor-pointer"
                  onClick={buyAnotherTicket}
                >
                  Make another donation
                </span>
              </>
            ) : (
              <>
                <h6 className="text-2xl md:text-3xl md:text-center">
                  I want to donate
                </h6>

                <div className="mt-4 flex flex-col md:items-center w-full">
                  <div className="btn-group">
                    {TICKETS.map(({ label }, index) => (
                      <button
                        key={index}
                        className={`btn md:btn-lg ${
                          index === selectedIndex
                            ? 'btn-secondary'
                            : 'border-neutral-700'
                        }`}
                        onClick={() => {
                          trackCustomEvent({
                            category: 'Tickets',
                            action: AnalyticsEvent.SelectDonation,
                            label,
                          });
                          setSelectedIndex(index);
                          setTicket(TICKETS[index]);
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {selectedIndex === TICKETS.length - 1 && (
                    <p className="mt-1 text-sm opacity-70 md:text-center">
                      *minimum donation is $10
                    </p>
                  )}
                  {statistic && !error && (
                    <>
                      <p className="mt-2 opacity-70 md:text-center">
                        People, you’re amazing! Our average donation is{' '}
                        {averageDonation}.
                      </p>
                    </>
                  )}

                  <div className="relative w-full h-12 flex justify-center items-center">
                    {transitions((style, item) =>
                      item?.description ? (
                        <animated.div
                          className="absolute badge border-secondary text-secondary h-auto text-center"
                          style={style}
                        >
                          {item?.description}
                        </animated.div>
                      ) : null
                    )}
                  </div>

                  {selectedIndex !== null && (
                    <>
                      <div className="mt-2 flex flex-col max-w-md">
                        <textarea
                          className="textarea textarea-bordered"
                          placeholder="Leave a message for the team (optional)"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                        />

                        <label
                          htmlFor={OPEN_COLLECTIVE_EMBED_MODAL_ID}
                          className="mt-4 btn btn-primary modal-button"
                          onClick={onProceed}
                        >
                          Proceed to payment
                        </label>

                        <p className="mt-2 md:text-center text-sm opacity-70">
                          🔒 Payments are securely processed through{' '}
                          <OutboundLink
                            className="underline underline-offset-4"
                            href="https://opencollective.com/devfest-for-ukraine/events/devfest-for-ukraine-93217149"
                            target="_blank"
                          >
                            Open Collective
                          </OutboundLink>
                          .
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="mt-4 md:mt-8 py-4 md:py-8 flex flex-col md:items-center border-y-2 border-neutral-800">
            <div className="md:w-10/12 lg:w-8/12 xl:w-7/12">
              <p className="md:text-center text-xl md:text-2xl">
                Setup for ticketing platform for donations is in progress
              </p>
              <p className="mt-2 md:mt-4 md:text-center text-lg opacity-70">
                In the meantime, subscribe to get notified when tickets would be
                available.
              </p>
            </div>

            <label
              htmlFor="subscribe-modal"
              className="mt-4 md:mt-8 btn btn-primary modal-button"
              onClick={() => {
                trackCustomEvent({
                  category: 'Tickets',
                  action: AnalyticsEvent.OpenModal,
                  label: 'Subscribe',
                });
              }}
            >
              Subscribe
            </label>
          </div>
        )} */}
      </div>
      <OpenCollectiveEmbedModal
        ticketSlug={
          isModalOpened && selectedTicket ? selectedTicket.slug : null
        }
        onClose={onCloseModal}
      />
    </>
  );
};
